<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/composables/useToast';
import { useMapGetter } from 'dashboard/composables/store';
import ConversionEventsAPI from 'dashboard/api/conversionEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');

// Vocabulário fixo e universal (decisão de produto): um só conjunto de tipos
// pra qualquer conta. Fonte/meio são DERIVADOS do tipo e travados no form —
// o bloco "como entra nos relatórios" só dá transparência ao mapeamento.
const TYPES = [
  {
    key: 'INDICACAO',
    identifier: 'manual-indicacao',
    source: 'indicacao',
    medium: 'referral',
  },
  {
    key: 'MIDIA_OFFLINE',
    identifier: 'manual-midia-offline',
    source: 'midia-offline',
    medium: 'offline',
  },
  {
    key: 'EVENTO',
    identifier: 'manual-evento',
    source: 'evento',
    medium: 'offline',
  },
  {
    key: 'WALK_IN',
    identifier: 'manual-walk-in',
    source: 'walk-in',
    medium: 'offline',
  },
  {
    key: 'PARCERIA',
    identifier: 'manual-parceria',
    source: 'parceria',
    medium: 'referral',
  },
  // Escape hatch: origem que nao cabe na lista fixa. Destrava os cinco UTMs
  // para quem sabe o que esta fazendo, sem inflar o vocabulario padrao.
  {
    key: 'PERSONALIZADO',
    identifier: 'manual-personalizado',
    source: '',
    medium: '',
    custom: true,
  },
];

const today = () => new Date().toISOString().slice(0, 10);

const selectedType = ref(TYPES[0].key);
const campaign = ref('');
const utmTerm = ref('');
const utmContent = ref('');
const occurredAt = ref(today());
const showAdvanced = ref(false);
const customSource = ref('');
const customMedium = ref('');
const isSubmitting = ref(false);

const contactId = computed(() => props.contact?.id || props.contact?.contactId);
const contactName = computed(() => props.contact?.name || '');

const activeType = computed(
  () => TYPES.find(item => item.key === selectedType.value) || TYPES[0]
);

const isCustom = computed(() => !!activeType.value.custom);

// No personalizado a fonte e obrigatoria: sem ela o evento entra sem origem e
// vira exatamente o "sem referencia" que a gente tenta reduzir.
const canSubmit = computed(
  () =>
    !!contactId.value &&
    !!selectedType.value &&
    !isSubmitting.value &&
    (!isCustom.value || !!customSource.value.trim())
);

// Normalização leve pra campanha não virar dimensão duplicada nos relatórios
// ("Feira ABC" vs "feira abc"): minúsculas + espaços viram hífen.
const normalizeDimension = value =>
  value.trim().toLowerCase().replace(/\s+/g, '-');

const resetForm = () => {
  selectedType.value = TYPES[0].key;
  campaign.value = '';
  utmTerm.value = '';
  utmContent.value = '';
  occurredAt.value = today();
  showAdvanced.value = false;
  customSource.value = '';
  customMedium.value = '';
};

const onClose = () => {
  emit('close');
};

const submit = async () => {
  if (!canSubmit.value) return;

  isSubmitting.value = true;
  try {
    const payload = {
      conversion_identifier: activeType.value.identifier,
      contact_id: contactId.value,
      platform: 'manual',
      utm_source: isCustom.value
        ? normalizeDimension(customSource.value)
        : activeType.value.source,
      utm_medium: isCustom.value
        ? normalizeDimension(customMedium.value)
        : activeType.value.medium,
      occurred_at: occurredAt.value,
      custom_attributes: {
        registrado_por: currentUser.value?.name || '',
      },
    };
    if (campaign.value.trim()) {
      payload.utm_campaign = normalizeDimension(campaign.value);
    }
    if (utmTerm.value.trim()) {
      payload.utm_term = normalizeDimension(utmTerm.value);
    }
    if (utmContent.value.trim()) {
      payload.utm_content = normalizeDimension(utmContent.value);
    }

    await ConversionEventsAPI.createManual(payload);
    toast.success(t('MANUAL_CONVERSION.SUCCESS'));
    resetForm();
    emit('created');
    emit('close');
  } catch (error) {
    const message =
      error?.response?.data?.error || t('MANUAL_CONVERSION.ERROR');
    toast.error(message);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('MANUAL_CONVERSION.TITLE')"
        :header-content="
          contactName
            ? $t('MANUAL_CONVERSION.SUBTITLE', { name: contactName })
            : ''
        "
      />
      <form class="flex flex-col gap-4 px-8 pt-4 pb-8" @submit.prevent="submit">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('MANUAL_CONVERSION.TYPE_LABEL') }}
          </label>
          <select
            v-model="selectedType"
            class="w-full !mb-0"
            data-testid="manual-conversion-type"
          >
            <option v-for="type in TYPES" :key="type.key" :value="type.key">
              {{ $t(`MANUAL_CONVERSION.TYPES.${type.key}`) }}
            </option>
          </select>
        </div>

        <!-- Como entra nos relatórios: fonte/meio travados pelo tipo + campanha editável -->
        <div class="rounded-lg border border-n-weak bg-n-slate-2 p-3">
          <div
            class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2"
          >
            {{ $t('MANUAL_CONVERSION.PREVIEW_TITLE') }}
          </div>
          <div class="grid grid-cols-2 gap-3 mb-3">
            <div>
              <span class="block text-[11px] text-n-slate-9 mb-0.5">
                {{ $t('MANUAL_CONVERSION.SOURCE_LABEL') }}
              </span>
              <input
                v-if="isCustom"
                v-model="customSource"
                type="text"
                class="w-full !mb-0 !h-8 !text-sm"
                :placeholder="$t('MANUAL_CONVERSION.SOURCE_PLACEHOLDER')"
              />
              <span
                v-else
                class="inline-flex items-center gap-1 text-sm font-medium text-n-slate-12"
              >
                <span class="i-lucide-lock size-3 text-n-slate-8" />
                {{ activeType.source }}
              </span>
            </div>
            <div>
              <span class="block text-[11px] text-n-slate-9 mb-0.5">
                {{ $t('MANUAL_CONVERSION.MEDIUM_LABEL') }}
              </span>
              <input
                v-if="isCustom"
                v-model="customMedium"
                type="text"
                class="w-full !mb-0 !h-8 !text-sm"
                :placeholder="$t('MANUAL_CONVERSION.MEDIUM_PLACEHOLDER')"
              />
              <span
                v-else
                class="inline-flex items-center gap-1 text-sm font-medium text-n-slate-12"
              >
                <span class="i-lucide-lock size-3 text-n-slate-8" />
                {{ activeType.medium }}
              </span>
            </div>
          </div>
          <label class="block text-[11px] text-n-slate-9 mb-0.5">
            {{ $t('MANUAL_CONVERSION.CAMPAIGN_LABEL') }}
          </label>
          <input
            v-model="campaign"
            type="text"
            class="w-full !mb-1"
            :placeholder="$t('MANUAL_CONVERSION.CAMPAIGN_PLACEHOLDER')"
          />
          <p class="text-[11px] text-n-slate-9 mb-0">
            {{ $t('MANUAL_CONVERSION.CAMPAIGN_HINT') }}
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('MANUAL_CONVERSION.DATE_LABEL') }}
          </label>
          <input
            v-model="occurredAt"
            type="date"
            :max="today()"
            class="w-full !mb-0"
          />
        </div>

        <!-- Avançado: utm_term / utm_content (raro em conversão manual).
             No tipo Personalizado eles ja vem abertos — quem escolheu preencher
             tudo na mao nao deveria caçar dois campos atras de um acordeao. -->
        <div>
          <button
            v-if="!isCustom"
            type="button"
            class="flex items-center gap-1 text-xs font-medium text-n-slate-10 hover:text-n-slate-12 transition-colors"
            @click="showAdvanced = !showAdvanced"
          >
            <span
              class="size-3.5 transition-transform"
              :class="
                showAdvanced
                  ? 'i-lucide-chevron-down'
                  : 'i-lucide-chevron-right'
              "
            />
            {{ $t('MANUAL_CONVERSION.ADVANCED') }}
          </button>
          <div
            v-if="showAdvanced || isCustom"
            class="grid grid-cols-2 gap-3 mt-2"
          >
            <div>
              <label class="block text-[11px] text-n-slate-9 mb-0.5">
                {{ $t('MANUAL_CONVERSION.TERM_LABEL') }}
              </label>
              <input v-model="utmTerm" type="text" class="w-full !mb-1" />
              <p class="text-[10px] text-n-slate-8 mb-0">
                {{ $t('MANUAL_CONVERSION.TERM_HINT') }}
              </p>
            </div>
            <div>
              <label class="block text-[11px] text-n-slate-9 mb-0.5">
                {{ $t('MANUAL_CONVERSION.CONTENT_LABEL') }}
              </label>
              <input v-model="utmContent" type="text" class="w-full !mb-1" />
              <p class="text-[10px] text-n-slate-8 mb-0">
                {{ $t('MANUAL_CONVERSION.CONTENT_HINT') }}
              </p>
            </div>
          </div>
        </div>

        <p class="text-xs text-n-slate-9 mb-0">
          {{ $t('MANUAL_CONVERSION.FOOTNOTE') }}
        </p>

        <div class="flex items-center justify-end gap-2 mt-2">
          <NextButton
            faded
            slate
            sm
            type="button"
            :label="$t('MANUAL_CONVERSION.CANCEL')"
            :disabled="isSubmitting"
            @click="onClose"
          />
          <NextButton
            sm
            type="submit"
            :label="$t('MANUAL_CONVERSION.SUBMIT')"
            :is-loading="isSubmitting"
            :disabled="!canSubmit"
            data-testid="manual-conversion-submit"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
